/**
 * Per-window identity for load-hub persistence.
 *
 * Each window reads/writes its OWN namespaced localStorage keys (see ./localStorage.ts), so editing a filter
 * in one monitor never leaks into another. The id must:
 *   - survive a RELOAD of the same tab (so the window keeps its own filters), yet
 *   - be UNIQUE per live window — even when the tab was DUPLICATED or dragged to another monitor, which makes
 *     Chrome copy sessionStorage (so a naive sessionStorage id would be shared and defeat the isolation).
 *
 * We reconcile the two with a liveness claim in localStorage: every live window heartbeats its id, and on
 * mount a window that finds its (copied) id already held by another LIVE window forks a fresh one. A reload
 * releases the claim on unload, so the reopened tab reclaims the same id (and its filters).
 */

const WINDOW_ID_KEY = 'private_loads_window_id'
const CLAIMS_KEY = 'private_loads_window_claims'
const HEARTBEAT_MS = 3000
// A claim counts as held by a still-live window only if its heartbeat landed within this window.
const LIVE_THRESHOLD_MS = 8000
// Drop claims no window has heartbeated in this long, so the registry can't grow unbounded.
const CLAIM_GC_MS = 60000

// One token per page-load instance (NOT persisted) — lets us tell our own claim apart from another window's.
const instanceToken = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`

let memoWindowId: string | null = null
let heartbeatTimer: ReturnType<typeof setInterval> | null = null
let lifecycleBound = false

const generateId = (): string => `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`

type Claim = { token: string; ts: number }

const readClaims = (): Record<string, Claim> => {
    try {
        const stored = localStorage.getItem(CLAIMS_KEY)
        return stored ? JSON.parse(stored) : {}
    } catch {
        return {}
    }
}

const writeClaims = (claims: Record<string, Claim>) => {
    try {
        localStorage.setItem(CLAIMS_KEY, JSON.stringify(claims))
    } catch {
        /* storage unavailable — ignore */
    }
}

/** Mark this id as owned by this instance, now (heartbeat), and GC long-dead claims. */
const claimId = (id: string) => {
    const claims = readClaims()
    const now = Date.now()
    claims[id] = { token: instanceToken, ts: now }
    for (const key of Object.keys(claims)) {
        if (key !== id && now - claims[key].ts > CLAIM_GC_MS) delete claims[key]
    }
    writeClaims(claims)
}

/** Release the claim on unload — but only if we still own it, so we never stomp another live window. */
const releaseId = (id: string) => {
    const claims = readClaims()
    if (claims[id]?.token === instanceToken) {
        delete claims[id]
        writeClaims(claims)
    }
}

const stopHeartbeat = () => {
    if (heartbeatTimer) {
        clearInterval(heartbeatTimer)
        heartbeatTimer = null
    }
}

const startLifecycle = (id: string) => {
    if (typeof window === 'undefined') return
    // Only heartbeat while visible: a two-monitor pair are both 'visible' (visibility ≠ focus), so duplication
    // detection keeps working — but a minimized/background tab stops churning localStorage. On becoming visible
    // we refresh the claim immediately, then resume beating.
    const sync = () => {
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
            stopHeartbeat()
            return
        }
        claimId(id)
        if (!heartbeatTimer) heartbeatTimer = setInterval(() => claimId(id), HEARTBEAT_MS)
    }
    sync()
    if (lifecycleBound) return
    lifecycleBound = true
    // Release on unload so a RELOAD of this tab immediately reclaims its id (keeping its filters). A DUPLICATE
    // tab never owns this id — it forks a fresh one on mount — so it can't release ours.
    const release = () => {
        stopHeartbeat()
        releaseId(id)
    }
    window.addEventListener('pagehide', release)
    window.addEventListener('beforeunload', release)
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', sync)
}

/** This window's stable id (created once, then reused for the life of the tab; forked if a copy of our
 *  sessionStorage is detected running in another live window). */
export const getLoadHubWindowId = (): string => {
    if (memoWindowId) return memoWindowId
    let id: string
    try {
        const candidate = sessionStorage.getItem(WINDOW_ID_KEY) || ''
        const existing = candidate ? readClaims()[candidate] : undefined
        // Held by a DIFFERENT live window → our sessionStorage was copied (tab duplicated / dragged out) → fork.
        const takenByLiveWindow = !!existing && existing.token !== instanceToken && Date.now() - existing.ts < LIVE_THRESHOLD_MS
        id = !candidate || takenByLiveWindow ? generateId() : candidate
        try {
            sessionStorage.setItem(WINDOW_ID_KEY, id)
        } catch {
            /* ignore */
        }
    } catch {
        // sessionStorage blocked — stable ephemeral id for this page load (no isolation, but never crash).
        id = generateId()
    }
    memoWindowId = id
    claimId(id)
    startLifecycle(id)
    return id
}
